// src/components/NavBar/Navbar.js
import React, { useState, useEffect, useMemo } from "react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Badge,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  useMediaQuery,
  Paper,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import {
  HomeOutlined,
  KeyboardArrowLeftSharp,
  KeyboardArrowRightSharp,
  LiveHelpOutlined,
  PeopleOutline,
  NotificationsNone as NotificationsNoneIcon,
  InsertDriveFileOutlined,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  School as SchoolIcon,
  StorefrontOutlined,
  OpenInNewOutlined,
  BusinessOutlined,
} from "@mui/icons-material";
import WhiteListButton from "../WhiteListButton.js";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import HandshakeOutlined from "@mui/icons-material/HandshakeOutlined";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import useAuth from "../../Hooks/use-auth";
import routes from "../../Config/routes.js";
import { ConfigValue } from "../../Config/index.js";
import client from "../../API";
import styles from "./NavBar.module.css";
import logo from "../globalAssets/logo_placeholder.png";
import ListItemLink from "../ListItemLink.js";
import { drawerWidth } from "../../Utils/constants.js";
import ChatModal from "../../Pages/Dashboard/Users/chatmodal.js";
import AddBalanceDialog from "../tools/addBalance.js";
import ContractPopup from "./systemComponents/ContractPopup.js";
import UnifiedNotificationCenter from "../tools/UnifiedNotificationCenter.js";
import { EmojiEvents as TrophyIcon } from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";

// ═══════════════════════════════════════════════════════════
// TUTORIAL SYSTEM IMPORTS
// ═══════════════════════════════════════════════════════════
import { useTutorial } from "../../BlitzHowTo/tutorialContext";
import { getTutorialForRoute } from "../../BlitzHowTo/tutorialRegistry";

/*------------------------------------------------------------------
  Cookie Utilities
-------------------------------------------------------------------*/
const COOKIE_KEYS = {
  ACCOUNT_STATUS: 'blitz_account_status',
  COLLABS_ACCESS: 'blitz_collabs_access',
  SUPERVISOR: 'blitz_supervisor',
};

const SURVEY_SEEN_KEY_PREFIX = "blitz_seen_survey";

const getSurveySeenKey = (userId, survey) => {
  const surveyKey = survey?.id || survey?.title || survey?.appears_on;
  return `${SURVEY_SEEN_KEY_PREFIX}:${userId || "anonymous"}:${surveyKey}`;
};

const hasSeenSurvey = (userId, survey) => {
  if (typeof window === "undefined" || !survey) return false;
  return window.localStorage.getItem(getSurveySeenKey(userId, survey)) === "true";
};

const markSurveySeen = (userId, survey) => {
  if (typeof window === "undefined" || !survey) return;
  window.localStorage.setItem(getSurveySeenKey(userId, survey), "true");
};

const cookieUtils = {
  set: (key, value, days = 30) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${key}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  },
  
  get: (key) => {
    const name = key + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    
    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.indexOf(name) === 0) {
        return cookie.substring(name.length);
      }
    }
    return null;
  },
  
  remove: (key) => {
    document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }
};

/*------------------------------------------------------------------
  1) Styled Components / MUI Helpers
-------------------------------------------------------------------*/
const WhiteIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.04)",
  },
}));

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} - 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} - 1px)`,
  },
});

const IconDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "extended",
})(({ theme, extended }) => ({
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(extended && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!extended && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

/** Helper: Combine a ListItemLink with a tooltip + (optionally) a badge. */
const ListItemLinkWithTooltip = ({
  to,
  primary,
  icon,
  tooltip,
  isOpen = true,
  count,
  endAdornment,
}) => (
  <Tooltip title={tooltip} placement="right">
    <ListItem disablePadding sx={{ display: "block" }}>
      <ListItemLink
        to={to}
        primary={primary}
        icon={
          count !== undefined && count > 0 ? (
            <Badge badgeContent={count} color="secondary">
              {icon}
            </Badge>
          ) : (
            icon
          )
        }
        isOpen={isOpen}
        endAdornment={endAdornment}
      />
    </ListItem>
  </Tooltip>
);

/*Enhanced Bottom Section Card*/
const BottomSectionCard = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(1.5),
  borderRadius: 12,
  boxShadow: `0 2px 8px ${
    theme.palette.mode === "dark" ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)"
  }`,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

/*------------------------------------------------------------------
  2) Survey Popup Component (optional)
-------------------------------------------------------------------*/
function SurveyPopup({ open, survey, userId, onClose }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const createResponseMutation = useMutation((payload) =>
    client.surveys.createResponse(payload),
  );

  useEffect(() => {
    if (survey) setAnswers({});
  }, [survey]);

  const handleAnswerChange = (index, value) => {
    setAnswers((prev) => ({ ...prev, [index]: value }));
  };

  const handleSubmit = async () => {
    try {
      await createResponseMutation.mutateAsync({
        survey_id: survey.id,
        user_id: userId,
        answers,
      });

      if (survey.action) {
        navigate(survey.action);
      }
      onClose(true);
    } catch (err) {
      console.error("Error submitting survey response:", err);
      alert("Error submitting survey response.");
      onClose(false);
    }
  };

  if (!survey) return null;

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>{survey.title || "Survey"}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {survey.description}
        </Typography>
        {survey.questions?.map((q, index) => (
          <Box key={index} mb={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              {q.question}
            </Typography>
            <TextField
              label="Your Answer"
              variant="outlined"
              fullWidth
              value={answers[index] || ""}
              onChange={(e) => handleAnswerChange(index, e.target.value)}
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createResponseMutation.isLoading}
        >
          {createResponseMutation.isLoading ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/*------------------------------------------------------------------
  NAVBAR COMPONENT
-------------------------------------------------------------------*/
const Navbar = ({
  isDesktop = true,
  isOpen = true,
  onOpen,
  onClose,
  onGetMobileActions,
  notificationAnchor,
  onSetNotificationAnchor,
}) => {
  const { getCurrrentUser, isAuthorized, unauthorize } = useAuth();
  const userInfo = getCurrrentUser();
  const userId = userInfo?.id;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const theme = useTheme();

  const [chatOpen, setChatOpen] = useState(false);

  // ═══════════════════════════════════════════════════════════
  // TUTORIAL SYSTEM HOOK
  // ═══════════════════════════════════════════════════════════
  const { startTutorial, resetTutorial } = useTutorial();

  // Enhanced responsive breakpoints for better desktop handling
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg")); // 1200px+
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("md", "lg")); // 900px-1200px
  const isSmallDesktop = useMediaQuery(
    theme.breakpoints.between("sm", "md"),
  ); // 600px-900px
  const isVeryTall = useMediaQuery("(min-height: 800px)");
  const isTall = useMediaQuery("(min-height: 700px)");

  // State for collapsible sections
  const [bottomSectionExpanded, setBottomSectionExpanded] = useState(
    isLargeScreen && isVeryTall,
  );

  // Initialize companyData from cookies for immediate access
  const [companyData, setCompanyData] = useState(() => {
    const cachedAccountStatus = cookieUtils.get(COOKIE_KEYS.ACCOUNT_STATUS);
    const cachedCollabsAccess = cookieUtils.get(COOKIE_KEYS.COLLABS_ACCESS);
    
    return {
      account_status: cachedAccountStatus || "",
      collabs_access: cachedCollabsAccess === "true",
    };
  });

  useQuery(
    ["company", userInfo?.company_id],
    () => client.companies.listFetch({ id: userInfo?.company_id }),
    {
      enabled: !!userInfo?.company_id,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 30,
      onSuccess: (data) => {
        const company =
          (Array.isArray(data) ? data[0] : null) || data?.data || data || {};
        
        const newAccountStatus = company.account_status || "";
        const newCollabsAccess = !!company.collabs_access;
        
        // Update state
        setCompanyData({
          account_status: newAccountStatus,
          collabs_access: newCollabsAccess,
        });
        
        // Persist to cookies
        if (newAccountStatus) {
          cookieUtils.set(COOKIE_KEYS.ACCOUNT_STATUS, newAccountStatus);
        }
        cookieUtils.set(COOKIE_KEYS.COLLABS_ACCESS, newCollabsAccess.toString());
        
        if (company.supervisor) {
          setSupervisor(company.supervisor);
          // Persist supervisor to cookies
          cookieUtils.set(COOKIE_KEYS.SUPERVISOR, JSON.stringify(company.supervisor));
        }
      },
      onError: (error) => {
        console.error("Error fetching company data:", error);
      },
    },
  );

  // Load user settings for font and color preferences
  const { data: userSettings } = useQuery(
    ["userSettingsNavbar"],
    client.users.fetchUserSettings,
    { enabled: isAuthorized, staleTime: 1000 * 60 * 60 * 12 },
  );

  // Compute user's preferred font family
  const fontPreference = userSettings?.font_preference || "default";
  const userFontFamily = {
    roboto: '"Roboto", "Helvetica", "Arial", sans-serif',
    "open-sans": '"Open Sans", sans-serif',
    montserrat: '"Montserrat", sans-serif',
    default: "Inter, Arial, sans-serif",
  }[fontPreference];

  // For controlling "extended" vs. collapsed drawer
  const isExtended = !isDesktop || isOpen;

  // Auto-collapse bottom section on smaller screens
  useEffect(() => {
    if (isLargeScreen && isVeryTall) {
      setBottomSectionExpanded(false);
    } else if (isMediumScreen && isTall) {
      setBottomSectionExpanded(false);
    } else {
      setBottomSectionExpanded(false);
    }
  }, [isLargeScreen, isMediumScreen, isVeryTall, isTall]);

  // ------------------- 1) Fetch Balance Data ---------------------
  const [balanceData, setBalanceData] = useState({
    balance: "Loading...",
  });
  
  // Initialize supervisor from cookies for immediate access
  const [supervisor, setSupervisor] = useState(() => {
    const cachedSupervisor = cookieUtils.get(COOKIE_KEYS.SUPERVISOR);
    if (cachedSupervisor) {
      try {
        return JSON.parse(cachedSupervisor);
      } catch (e) {
        console.error("Error parsing cached supervisor:", e);
        return null;
      }
    }
    return null;
  });

  useQuery(
    ["balance", "my_balance"],
    () => client.balanceLedger.fetchMyBalance(),
    {
      onSuccess: (data) => {
        if (data?.balance !== undefined) {
          setBalanceData({
            balance: `$${parseFloat(data.balance).toFixed(2)}`,
          });
        } else {
          console.error("Unexpected balance data format:", data);
          setBalanceData({
            balance: "Error loading balance",
          });
        }
      },
      onError: (error) => {
        console.error("Error fetching balance data:", error);
        setBalanceData({
          balance: "Error loading balance",
        });
      },
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 30,
      enabled: !!userInfo,
    },
  );

  // Fetch company data for supervisor info (kept as-is, even if somewhat redundant)
  useQuery(
    ["company", userInfo?.company_id],
    () => client.companies.listFetch({ id: userInfo?.company_id }),
    {
      onSuccess: (data) => {
        if (data.supervisor) {
          setSupervisor(data.supervisor);
          // Persist supervisor to cookies
          cookieUtils.set(COOKIE_KEYS.SUPERVISOR, JSON.stringify(data.supervisor));
        }
      },
      onError: (error) => {
        console.error("Error fetching company data:", error);
      },
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 30,
      enabled: !!userInfo,
    },
  );

  // ------------------- 3) Unread Collaboration Notifications -------------
  const {
    data: unreadCollabCountData,
    isLoading: isLoadingUnreadCollabs,
    refetch: refetchUnreadCollabs,
  } = useQuery(
    ["notifications", "unread_collab_count"],
    () => client.notifications.legacy.getUnreadCollabCount(),
    {
      enabled: !!userId,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    },
  );

  const unreadCollabNotificationsCount =
    unreadCollabCountData?.unread_collab_count || 0;

  // ------------------- 4) Notification (Bell) Logic - Now using v2 ----------------------
  const {
    data: unreadNotifCountData,
    isLoading: isLoadingUnread,
  } = useQuery(
    ["notifications", "v2", "unread_count"],
    () => client.notifications.getUnreadCount(),
    {
      enabled: !!userId,
      staleTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    },
  );

  const unreadNotificationCount = unreadNotifCountData?.unread_count || 0;

  const handleOpenNotifications = (e) => {
    onSetNotificationAnchor?.(e.currentTarget);
  };

  const handleCloseNotifications = () => {
    onSetNotificationAnchor?.(null);
  };

  // ═══════════════════════════════════════════════════════════
  // TUTORIAL MANUAL TRIGGER HANDLER
  // ═══════════════════════════════════════════════════════════
  const handleShowTutorial = () => {
    const tutorial = getTutorialForRoute(location.pathname);
    if (tutorial) {
      resetTutorial(tutorial.id);
      startTutorial(tutorial);
    } else {
      // No tutorial for this route - could show a message
      console.log('No tutorial available for this page');
    }
  };

  // Check if current route has a tutorial
  const currentTutorial = useMemo(() => {
    return getTutorialForRoute(location.pathname);
  }, [location.pathname]);

  // ------------------- Mobile Actions Logic ----------------------
  const mobileActions = useMemo(
    () => [
      {
        key: "notifications",
        icon: (
          <Badge
            badgeContent={isLoadingUnread ? 0 : unreadNotificationCount}
            color="secondary"
          >
            <NotificationsNoneIcon />
          </Badge>
        ),
        onClick: handleOpenNotifications,
        tooltip: "Notifications",
      },
      {
        key: "trophy",
        icon: <TrophyIcon color="warning" />,
        onClick: () => navigate(routes.trophyRoom),
        tooltip: "Trophy Room",
      },
      {
        key: "settings",
        icon: <SettingsOutlinedIcon />,
        onClick: () => navigate(routes.user),
        tooltip: "Account settings",
      },
    ],
    [
      unreadNotificationCount,
      isLoadingUnread,
      navigate,
    ],
  );

  // Send mobile actions to parent when on mobile
  useEffect(() => {
    if (!isDesktop && onGetMobileActions) {
      onGetMobileActions(mobileActions);
    }
  }, [isDesktop, mobileActions, onGetMobileActions]);

  // ------------------- 5) Financial Menu ----------------
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  // Add Balance Dialog
  const [addBalanceDialogOpen, setAddBalanceDialogOpen] = useState(false);
  const handleAddBalance = () => {
    setAddBalanceDialogOpen(true);
    handleCloseMenu();
  };
  const handleCloseAddBalanceDialog = () => {
    setAddBalanceDialogOpen(false);
  };

  // ------------------- 7) Fetch Pending Surveys ----------------
  const { data: pendingSurveys } = useQuery(
    ["pendingSurveys", userId],
    () => client.surveys.listPending(userId),
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
    },
  );

  const [activeSurvey, setActiveSurvey] = useState(null);

  useEffect(() => {
    if (!pendingSurveys) return;
    const match = pendingSurveys.find(
      (s) => s.appears_on === location.pathname && !hasSeenSurvey(userId, s),
    );
    setActiveSurvey(match || null);
  }, [pendingSurveys, location.pathname, userId]);

  const handleCloseSurvey = (didComplete) => {
    markSurveySeen(userId, activeSurvey);
    setActiveSurvey(null);
    if (didComplete) {
      queryClient.invalidateQueries(["pendingSurveys", userId]);
    }
  };

  // ------------------- 7.5) Fetch Pending Contracts ----------------
  const { data: pendingContracts } = useQuery(
    ["pendingContracts", userId],
    () => client.contracts.getMyPendingContracts(),
    {
      enabled: !!userId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 30,
    },
  );

  const [activeContracts, setActiveContracts] = useState(null);
  const [contractPopupDismissed, setContractPopupDismissed] = useState(false);

  useEffect(() => {
    if (!pendingContracts?.pending_contracts || contractPopupDismissed) return;

    // Only show contracts popup on dashboard route
    if (location.pathname !== routes.dashboard) {
      setActiveContracts(null);
      return;
    }

    const contracts = pendingContracts.pending_contracts;
    if (contracts.length > 0) {
      setActiveContracts(contracts);
    } else {
      setActiveContracts(null);
    }
  }, [pendingContracts, contractPopupDismissed, location.pathname]);

  const handleCloseContractPopup = () => {
    setActiveContracts(null);
    setContractPopupDismissed(true);
    // Reset dismissal after 1 hour
    setTimeout(() => setContractPopupDismissed(false), 1000 * 60 * 60);
  };

  // ------------------- 8) Build the main nav items -----------------------
  // Blitz Commerce (Alpha) is limited to AlphaFree accounts for now.
  // CRM (formerly "Account Management") is visible under that same
  // AlphaFree gate, in addition to the existing supervisor-only path.
  const isAlphaFreeAccount = companyData.account_status === "AlphaFree";

  // Money/Search/Chat/CRM are now fully owned by the Next.js app (/money,
  // /search, /messages, /am) — removed from this nav so legacy sessions
  // don't organically rediscover pages that have moved. Commerce stays (no
  // Next.js UI yet); Collabs stays (not yet confirmed live on Next.js).
  // Admin Dashboard stays too — legacy's admin tooling isn't a confirmed
  // full replacement by Next.js's /admin yet. See docs/legacy-fallback-pages.md.
  const listItems = [];

  // Blitz Commerce — product management, links, and sales/CRM metrics.
  // Alpha-gated to AlphaFree accounts.
  if (isAlphaFreeAccount) {
    listItems.push({
      to: routes.commerce,
      primary: "Commerce",
      icon: <StorefrontOutlined />,
      tooltip: "Create product links and track sales, purchasers, and returns",
    });
  }

  listItems.push({
    to: routes.collaborations,
    primary: "Collabs",
    icon: <HandshakeOutlined />,
    tooltip: "Manage collaborations and projects",
    count: isLoadingUnreadCollabs ? undefined : unreadCollabNotificationsCount,
  });

  // Add Company Management for admin users
  if (userInfo?.type === "admin") {
    listItems.push({
      to: routes.company,
      primary: "Admin Dashboard",
      icon: <BusinessOutlined />,
      tooltip:
        "Admin dashboard for managing companies and system operations",
    });
  }

  /*------------------------------------------------------------------
     RENDER
  ------------------------------------------------------------------*/
  const displayName =
    `${userInfo?.first_name} ${userInfo?.last_name}`.trim() ||
    userInfo?.username ||
    "";
  const status = userInfo?.company_name || "";
  const photo = userInfo?.phphref;

  return (
    <>
      <IconDrawer
        id="NavbarContainer"
        variant={isDesktop ? "permanent" : "temporary"}
        anchor={isDesktop ? "left" : "right"}
        sx={{
          fontFamily: userFontFamily,
          color: theme.palette.text.primary,
          "& .MuiDrawer-paper": {
            fontFamily: userFontFamily,
            color: theme.palette.text.primary,
            paddingTop: {
              xs: `calc(env(safe-area-inset-top, 0px) + ${theme.spacing(1)})`,
              md: 0,
            },
            boxSizing: "border-box",
            backgroundColor: theme.palette.background.default,
            backgroundImage: "none !important",
            overflowY: "hidden",
            scrollbarColor: `${theme.palette.text.primary} ${theme.palette.background.default}`,
            scrollbarWidth: "thin",
            maxHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
        open={isDesktop || isOpen}
        extended={isExtended}
        onClose={onClose}
      >
        {/* HEADER / LOGO */}
        <Box
          className={styles.navbarHeader}
          sx={{
            fontFamily: userFontFamily,
            marginInline: 2,
            marginBlockStart: 2,
            display: { xs: "none", md: "flex" },
            flexDirection: "row",
            alignItems: "center",
            justifyContent: isDesktop
              ? isOpen
                ? "space-between"
                : "center"
              : "space-between",
            flexShrink: 0,
          }}
        >
          <Box
            className={styles.logo}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {isOpen && (
              <>
                <RouterLink
                  to={isAuthorized ? routes.dashboard : routes.dashboard}
                >
                  <img src={logo} alt="Logo" />
                </RouterLink>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                >
                  Business Suite
                </Typography>
              </>
            )}
          </Box>
          <WhiteIconButton onClick={() => (isOpen ? onClose() : onOpen())}>
            {isOpen ? <KeyboardArrowLeftSharp /> : <KeyboardArrowRightSharp />}
          </WhiteIconButton>
        </Box>

        {isDesktop && (
          <Divider
            sx={{ marginBlockStart: 2, marginBlockEnd: 1, flexShrink: 0 }}
          />
        )}

        {/* Persistent escape hatch back to the Next.js app — every other
            feature has moved there; this stays visible regardless of what
            page within blitz the user landed on (Commerce, a stale
            bookmark, etc). Placed near the top so it's immediately visible,
            not buried at the bottom. Goes to /money (the app itself), not
            the bare landing page — anyone using this is already
            authenticated. See docs/legacy-fallback-pages.md. */}
        <Tooltip title="Go to the Blitz app" placement="right">
          <Box
            onClick={() => {
              window.location.href = ConfigValue.NEXT_APP_URL
                ? `${ConfigValue.NEXT_APP_URL}/money`
                : routes.base;
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mx: isExtended ? 2 : 1.5,
              mb: 1,
              px: isExtended ? 1.5 : 0,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              color: "text.secondary",
              justifyContent: isExtended ? "flex-start" : "center",
              flexShrink: 0,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <OpenInNewOutlined fontSize="small" />
            {isExtended && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Back to Blitz
              </Typography>
            )}
          </Box>
        </Tooltip>

        {/* Enhanced Profile Section */}
        <Box
          sx={{
            p: isExtended ? 2 : 1.5,
            display: "flex",
            flexDirection: isExtended
              ? isDesktop
                ? "row"
                : "column"
              : "column",
            alignItems: isExtended && isDesktop ? "flex-start" : "center",
            gap: isExtended ? 1.5 : 1,
            minHeight: isExtended ? "auto" : 120,
            justifyContent: isExtended ? "flex-start" : "center",
            flexShrink: 0,
          }}
        >
          {/* Enhanced Avatar with Bio Navigation */}
          <Tooltip title={`Go to ${displayName}'s bio`} placement="right" arrow>
            <Avatar
              src={photo}
              alt={displayName}
              sx={{
                width: isExtended ? 56 : 48,
                height: isExtended ? 56 : 48,
                cursor: "pointer",
                flexShrink: 0,
                border: "2px solid",
                borderColor: theme.palette.primary.main,
                boxShadow: theme.shadows[2],
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
              onClick={() => navigate("/bio")}
            />
          </Tooltip>

          {/* Profile Info & Actions Container */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: isExtended && isDesktop ? "flex-start" : "center",
              flex: isExtended ? 1 : "none",
              width: "100%",
              gap: isExtended ? 1 : 0.75,
            }}
          >
            {/* Profile Text - Only show in extended mode */}
            {isExtended && (
              <Box
                sx={{
                  textAlign: isDesktop ? "left" : "center",
                  width: "100%",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    lineHeight: 1.2,
                    mb: 0.25,
                  }}
                >
                  Hey, {displayName}!
                </Typography>
                {status && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      lineHeight: 1.2,
                      display: "block",
                    }}
                  >
                    {status}
                  </Typography>
                )}
              </Box>
            )}

            {/* Action Buttons Row */}
            <Box
              sx={{
                display: "flex",
                flexDirection: isExtended ? "row" : "column",
                alignItems: "center",
                justifyContent:
                  isExtended && isDesktop ? "space-between" : "center",
                gap: isExtended ? 0.5 : 0.75,
                width: isExtended ? "100%" : "auto",
              }}
            >
              {/* Left side buttons - vertical when minimized */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isExtended ? "row" : "column",
                  gap: isExtended ? 0.5 : 0.75,
                  alignItems: "center",
                }}
              >
                {/* Notifications Button */}
                <Tooltip
                  title="Notifications"
                  placement={isExtended ? "bottom" : "right"}
                  arrow
                >
                  <IconButton
                    onClick={handleOpenNotifications}
                    size={isExtended ? "medium" : "small"}
                    sx={{
                      color: "text.primary",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <Badge
                      badgeContent={
                        isLoadingUnread ? 0 : unreadNotificationCount
                      }
                      color="secondary"
                      max={99}
                    >
                      <NotificationsNoneIcon
                        fontSize={isExtended ? "medium" : "small"}
                      />
                    </Badge>
                  </IconButton>
                </Tooltip>

                {/* Trophy Room Button */}
                <Tooltip
                  title="Trophy Room"
                  placement={isExtended ? "bottom" : "right"}
                  arrow
                >
                  <IconButton
                    onClick={() => navigate(routes.trophyRoom)}
                    size={isExtended ? "medium" : "small"}
                    sx={{
                      color: "text.primary",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <TrophyIcon
                      fontSize={isExtended ? "medium" : "small"}
                      color="warning"
                    />
                  </IconButton>
                </Tooltip>

                {/* Settings Button */}
                <Tooltip
                  title="Account Settings"
                  placement={isExtended ? "bottom" : "right"}
                  arrow
                >
                  <IconButton
                    onClick={() => navigate(routes.user)}
                    size={isExtended ? "medium" : "small"}
                    sx={{
                      color: "text.primary",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <SettingsOutlinedIcon
                      fontSize={isExtended ? "medium" : "small"}
                    />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Right side - Logout Button (desktop extended only) */}
              {isExtended && isDesktop && (
                <Tooltip title="Log Out" placement="bottom" arrow>
                  <IconButton
                    onClick={() => {
                      // Clear cached company/supervisor data on logout
                      cookieUtils.remove(COOKIE_KEYS.ACCOUNT_STATUS);
                      cookieUtils.remove(COOKIE_KEYS.COLLABS_ACCESS);
                      cookieUtils.remove(COOKIE_KEYS.SUPERVISOR);
                      
                      unauthorize();
                      return window.location.href = routes.base;
                    }}
                    size="medium"
                    sx={{
                      color: "text.primary",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "error.main",
                        color: "white",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <LogoutIcon fontSize="medium" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        {isDesktop && isExtended && <Divider sx={{ flexShrink: 0 }} />}

        {/* MAIN NAV ITEMS */}
        <Box
          id="Icon-List"
          sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}
        >
          <Box
            id="Scrollable-Icon-List"
            sx={{
              flexGrow: 1,
              flexShrink: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "clip",
              scrollbarColor: `rgba(0,0,0,0.24) transparent`,
              scrollbarWidth: "thin",
              paddingBottom: theme.spacing(1),
            }}
          >
            <List disablePadding>
              {listItems.map((item) => (
                <ListItemLinkWithTooltip
                  key={item.to}
                  {...item}
                  isOpen={isExtended}
                />
              ))}
            </List>
          </Box>

          {/* ═══════════════════════════════════════════════════════════
              ENHANCED BOTTOM SECTION - WITH TUTORIAL TRIGGER
              ═══════════════════════════════════════════════════════════ */}
          <Box sx={{ flexShrink: 0 }}>
            <Divider />
            {/* Enhanced Bottom Section with Supervisor + Help + Tutorial */}
            {isExtended && (
              <Box sx={{ p: 1.5 }}>
                <BottomSectionCard>
                  {/* Supervisor Section */}
                  {supervisor && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        src={supervisor.phphref}
                        sx={{
                          width: 40,
                          height: 40,
                          border: `2px solid ${theme.palette.primary.main}`,
                          boxShadow: theme.shadows[2],
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            display: "block",
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {supervisor.first_name} {supervisor.last_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1 }}
                        >
                          Your Blitz Bestie ✨
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setBottomSectionExpanded(!bottomSectionExpanded)
                        }
                        sx={{
                          p: 0.5,
                          backgroundColor: theme.palette.action.hover,
                          "&:hover": {
                            backgroundColor: theme.palette.action.selected,
                          },
                        }}
                      >
                        {bottomSectionExpanded ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  )}

                  {/* Expandable Actions Section */}
                  <Collapse in={bottomSectionExpanded}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: supervisor ? 1.5 : 0,
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Supervisor Actions */}
                      {supervisor && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              navigate(`/profile/${supervisor.id}`)
                            }
                            sx={{
                              textTransform: "none",
                              fontSize: "0.75rem",
                              flex: "1 1 auto",
                              minWidth: "fit-content",
                            }}
                          >
                            Profile
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setChatOpen(true)}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.75rem",
                              flex: "1 1 auto",
                              minWidth: "fit-content",
                            }}
                          >
                            Chat
                          </Button>
                        </>
                      )}

                      {/* Help Button */}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<LiveHelpOutlined fontSize="small" />}
                        onClick={() => navigate("/help")}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          flex: supervisor ? "1 1 100%" : "1 1 auto",
                          minWidth: "fit-content",
                          borderColor: theme.palette.info.main,
                          color: theme.palette.info.main,
                          "&:hover": {
                            borderColor: theme.palette.info.dark,
                            backgroundColor: theme.palette.info.light + "20",
                          },
                        }}
                      >
                        Need Help?
                      </Button>

                      {/* ═══════════════════════════════════════════════
                          TUTORIAL TRIGGER BUTTON
                          ═══════════════════════════════════════════════ */}
                      {currentTutorial && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SchoolIcon fontSize="small" />}
                          onClick={handleShowTutorial}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.75rem",
                            flex: "1 1 100%",
                            minWidth: "fit-content",
                            borderColor: theme.palette.secondary.main,
                            color: theme.palette.secondary.main,
                            "&:hover": {
                              borderColor: theme.palette.secondary.dark,
                              backgroundColor: theme.palette.secondary.light + "20",
                            },
                          }}
                        >
                          Show Tutorial
                        </Button>
                      )}
                    </Box>
                  </Collapse>

                  {/* If no supervisor, show compact help + tutorial buttons */}
                  {!supervisor && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.info.light,
                          color: theme.palette.info.main,
                        }}
                      >
                        <LiveHelpOutlined />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          Need Help?
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Get support and answers
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="info"
                          onClick={() => navigate("/help")}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.75rem",
                          }}
                        >
                          Help
                        </Button>
                        {currentTutorial && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<SchoolIcon fontSize="small" />}
                            onClick={handleShowTutorial}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.65rem",
                            }}
                          >
                            Tutorial
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}
                </BottomSectionCard>
              </Box>
            )}
          </Box>
        </Box>

        {/* ADD BALANCE DIALOG */}
        <AddBalanceDialog
          open={addBalanceDialogOpen}
          onClose={handleCloseAddBalanceDialog}
        />
      </IconDrawer>

      {/* UNIFIED NOTIFICATION CENTER - Replaces old menu */}
      <UnifiedNotificationCenter
        anchorEl={notificationAnchor}
        onClose={handleCloseNotifications}
        isCreator={false}
      />

      {/* SURVEY POPUP */}
      {activeSurvey && (
        <SurveyPopup
          open={Boolean(activeSurvey)}
          survey={activeSurvey}
          userId={userId}
          onClose={handleCloseSurvey}
        />
      )}

      {/* CONTRACT POPUP - Now using imported component */}
      <ContractPopup
        open={Boolean(activeContracts)}
        contracts={activeContracts || []}
        onClose={handleCloseContractPopup}
      />

      {/* CHAT MODAL */}
      <ChatModal
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        recipientId={supervisor?.id}
      />
    </>
  );
};

export default Navbar;